import type { TradingConfig } from '@ai-trading/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { enableWebPush } from '../lib/push.js';

const indicators: TradingConfig['prompt_indicators'] = [
  'price_action',
  'funding',
  'open_interest',
  'volume',
  'recent_fills',
  'orderbook_top',
];

interface Subscription {
  interval_seconds: number;
  next_tick_at: number;
}

const paperIntervals = [
  { label: 'Every 5 minutes', value: 300 },
  { label: 'Every 15 minutes', value: 900 },
  { label: 'Every 1 hour', value: 3600 },
  { label: 'Every 4 hours', value: 14400 },
] as const;

const strategyNumberFields = [
  ['min_confidence_open', 'Min open confidence'],
  ['max_spread_bps', 'Max spread bps'],
  ['min_liquidity_score', 'Min liquidity score'],
  ['min_orderbook_imbalance_abs', 'Min OBI abs'],
  ['min_momentum_15m_pct', 'Min 15m momentum %'],
  ['min_volatility_1h_pct', 'Min 1h volatility %'],
  ['max_volatility_1h_pct', 'Max 1h volatility %'],
  ['min_volume_24h', 'Min 24h volume'],
  ['rsi_long_below', 'RSI long below'],
  ['rsi_short_above', 'RSI short above'],
] as const satisfies readonly [keyof TradingConfig['strategy'], string][];

export const Settings = () => {
  const qc = useQueryClient();
  const cfg = useQuery({
    queryKey: ['config'],
    queryFn: () => api.get<{ config: TradingConfig; defaults: boolean }>('/me/config'),
  });
  const sub = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.get<Subscription>('/me/subscription'),
  });
  const [draft, setDraft] = useState<TradingConfig | null>(null);
  const current = draft ?? cfg.data?.config ?? null;

  const save = useMutation({
    mutationFn: (next: TradingConfig) =>
      api.put<{ ok: boolean; config: TradingConfig; notes: string[] }>('/me/config', next),
    onSuccess: (r) => {
      setDraft(r.config);
      void qc.invalidateQueries({ queryKey: ['config'] });
    },
  });
  const startPaper = useMutation({
    mutationFn: async () => {
      const saved = await api.put<{ ok: boolean; config: TradingConfig; notes: string[] }>(
        '/me/config',
        { ...current, mode: 'paper' },
      );
      const started = await api.post<{ ok: boolean; next_tick_at: number }>(
        '/me/config/start-paper',
      );
      return { ...saved, next_tick_at: started.next_tick_at };
    },
    onSuccess: (r) => {
      setDraft(r.config);
      void qc.invalidateQueries({ queryKey: ['config'] });
      void qc.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
  const updateInterval = useMutation({
    mutationFn: (interval_seconds: number) =>
      api.put<{ ok: boolean; interval_seconds: number; next_tick_at: number }>(
        '/me/subscription/interval',
        { interval_seconds },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
  const probeTick = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; tick_id: string; scheduled_at: number }>('/me/config/probe-tick'),
  });

  if (!current) return <main className="page">Loading trading config…</main>;

  const update = <K extends keyof TradingConfig>(k: K, v: TradingConfig[K]) =>
    setDraft({ ...current, [k]: v });

  const toggleIndicator = (indicator: TradingConfig['prompt_indicators'][number]) => {
    const next = current.prompt_indicators.includes(indicator)
      ? current.prompt_indicators.filter((i) => i !== indicator)
      : [...current.prompt_indicators, indicator];
    update('prompt_indicators', next.length > 0 ? next : ['price_action']);
  };

  return (
    <main className="page">
      <header className="hero">
        <div>
          <p className="eyebrow">Paper trading launchpad</p>
          <h1>Set risk once. Let the AI run safely.</h1>
          <p className="lead">
            Pick a market, choose a risk profile, and start a paper loop with one click. Trial users
            stay in paper mode and every value is enforced again on the server.
          </p>
          <div className="row" style={{ justifyContent: 'flex-start', marginTop: '1.25rem' }}>
            <button
              className="btn btn-primary"
              type="button"
              disabled={startPaper.isPending}
              onClick={() => startPaper.mutate()}
            >
              {startPaper.isPending ? 'Starting paper loop…' : 'Start paper trading'}
            </button>
            <span className="badge badge-success">No real funds at risk</span>
          </div>
        </div>
        <div className="card panel-strong">
          <span className="label">Ready-to-run setup</span>
          <p className="stat-value">{current.assets.join(', ') || 'BTC'}</p>
          <div className="timeline">
            <div className="timeline-item">
              <span className="dot">1</span>
              <div>
                <strong>Paper mode</strong>
                <p className="muted">Starts with $10,000 simulated equity.</p>
              </div>
            </div>
            <div className="timeline-item">
              <span className="dot">2</span>
              <div>
                <strong>First tick</strong>
                <p className="muted">Scheduled immediately for the next cron run.</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="grid grid-2" style={{ marginTop: '1rem' }}>
        <div className="panel stack">
          <div className="section-header">
            <div>
              <h2>1. Market universe</h2>
              <p className="muted">Trial accounts are limited by server-side tier rules.</p>
            </div>
            <span className="badge">{current.market_kind.join(', ')}</span>
          </div>
          <label className="field">
            <span className="label">Assets</span>
            <input
              type="text"
              value={current.assets.join(',')}
              onChange={(e) =>
                update(
                  'assets',
                  e.target.value
                    .split(',')
                    .map((s) => s.trim().toUpperCase())
                    .filter(Boolean),
                )
              }
            />
          </label>
          <label className="field">
            <span className="label">Market type</span>
            <select
              value={current.market_kind[0] ?? 'perp'}
              onChange={(e) => update('market_kind', [e.target.value as 'perp' | 'spot'])}
            >
              <option value="perp">Perpetuals</option>
              <option value="spot">Spot</option>
            </select>
          </label>
        </div>

        <div className="panel stack">
          <div>
            <h2>2. Strategy behavior</h2>
            <p className="muted">Tell the model how it should think before risk gates execute.</p>
          </div>
          <div className="choice-grid">
            {(['long_only', 'short_only', 'both'] as const).map((bias) => (
              <label className="choice" key={bias}>
                <input
                  type="radio"
                  name="bias"
                  checked={current.bias === bias}
                  onChange={() => update('bias', bias)}
                />
                <strong>{bias.replace('_', ' ')}</strong>
              </label>
            ))}
          </div>
          <label className="field">
            <span className="label">Time horizon</span>
            <select
              value={current.time_horizon}
              onChange={(e) =>
                update('time_horizon', e.target.value as TradingConfig['time_horizon'])
              }
            >
              <option value="scalp">Scalp</option>
              <option value="swing">Swing</option>
              <option value="trend">Trend</option>
            </select>
          </label>
          <label className="field">
            <span className="label">Strategy preset</span>
            <select
              value={current.strategy.preset}
              onChange={(e) =>
                update('strategy', {
                  ...current.strategy,
                  preset: e.target.value as TradingConfig['strategy']['preset'],
                })
              }
            >
              <option value="orderbook_momentum">Orderbook momentum</option>
              <option value="volatility_momentum">Volatility momentum</option>
              <option value="liquidity_sweep_reversion">Liquidity sweep reversion</option>
              <option value="volume_oi_breakout">Volume/OI breakout</option>
              <option value="risk_off_filter">Risk-off filter</option>
            </select>
          </label>
        </div>
      </section>

      <section className="panel" style={{ marginTop: '1rem' }}>
        <div className="section-header">
          <div>
            <h2>3. Sizing & risk limits</h2>
            <p className="muted">These are hard caps, not suggestions.</p>
          </div>
          <span className="badge badge-warning">Server enforced</span>
        </div>
        <div className="form-grid">
          <label className="field">
            <span className="label">Equity % per trade</span>
            <input
              type="number"
              value={current.equity_pct_per_trade}
              onChange={(e) => update('equity_pct_per_trade', Number(e.target.value))}
            />
          </label>
          <label className="field">
            <span className="label">Max position %</span>
            <input
              type="number"
              value={current.max_position_pct}
              onChange={(e) => update('max_position_pct', Number(e.target.value))}
            />
          </label>
          <label className="field">
            <span className="label">Max leverage</span>
            <input
              type="number"
              value={current.max_leverage}
              onChange={(e) => update('max_leverage', Number(e.target.value))}
            />
          </label>
          <label className="field">
            <span className="label">Stop loss %</span>
            <input
              type="number"
              value={current.stop_loss_pct ?? ''}
              placeholder="Optional"
              onChange={(e) =>
                update('stop_loss_pct', e.target.value === '' ? null : Number(e.target.value))
              }
            />
          </label>
          <label className="field">
            <span className="label">Take profit %</span>
            <input
              type="number"
              value={current.take_profit_pct ?? ''}
              placeholder="Optional"
              onChange={(e) =>
                update('take_profit_pct', e.target.value === '' ? null : Number(e.target.value))
              }
            />
          </label>
          <label className="field">
            <span className="label">Max daily loss %</span>
            <input
              type="number"
              value={current.max_daily_loss_pct}
              onChange={(e) => update('max_daily_loss_pct', Number(e.target.value))}
            />
          </label>
          <label className="field">
            <span className="label">Max drawdown %</span>
            <input
              type="number"
              value={current.max_drawdown_pct}
              onChange={(e) => update('max_drawdown_pct', Number(e.target.value))}
            />
          </label>
        </div>
      </section>

      <section className="grid grid-2" style={{ marginTop: '1rem' }}>
        <div className="panel stack">
          <h2>4. AI inputs</h2>
          <label className="field">
            <span className="label">Model</span>
            <select
              value={current.model_id}
              onChange={(e) => update('model_id', e.target.value as TradingConfig['model_id'])}
            >
              <option value="deepseek/deepseek-v4-flash">DeepSeek V4 Flash</option>
            </select>
            <span className="muted">DeepSeek V4 Flash is the only selectable model for now.</span>
          </label>
          <div className="check-list">
            {indicators.map((indicator) => (
              <label className="check-pill" key={indicator}>
                <input
                  type="checkbox"
                  checked={current.prompt_indicators.includes(indicator)}
                  onChange={() => toggleIndicator(indicator)}
                />
                {indicator.replaceAll('_', ' ')}
              </label>
            ))}
          </div>
          <label className="field">
            <span className="label">Custom prompt prefix</span>
            <textarea
              value={current.custom_prompt_prefix ?? ''}
              placeholder="Optional pro prompt guidance"
              onChange={(e) => update('custom_prompt_prefix', e.target.value || null)}
            />
          </label>
        </div>

        <div className="panel stack">
          <h2>4b. HFT-inspired filters</h2>
          <p className="muted">
            These hard filters make entries more reactive without allowing thin or wide-spread
            markets.
          </p>
          <div className="form-grid">
            {strategyNumberFields.map(([key, label]) => (
              <label className="field" key={key}>
                <span className="label">{label}</span>
                <input
                  type="number"
                  step="0.01"
                  value={current.strategy[key as keyof TradingConfig['strategy']] as number}
                  onChange={(e) =>
                    update('strategy', {
                      ...current.strategy,
                      [key]: Number(e.target.value),
                    })
                  }
                />
              </label>
            ))}
          </div>
          <label className="check-pill">
            <input
              type="checkbox"
              checked={current.strategy.require_multi_signal}
              onChange={() =>
                update('strategy', {
                  ...current.strategy,
                  require_multi_signal: !current.strategy.require_multi_signal,
                })
              }
            />
            Require multi-signal agreement
          </label>
        </div>

        <div className="panel stack">
          <h2>5. Launch controls</h2>
          <div className="choice-grid">
            <label className="choice">
              <input
                type="radio"
                name="mode"
                checked={current.mode === 'paper'}
                onChange={() => update('mode', 'paper')}
              />
              <strong>Paper</strong>
              <p className="muted">Simulated fills, no real funds.</p>
            </label>
            <label className="choice">
              <input
                type="radio"
                name="mode"
                checked={current.mode === 'live'}
                onChange={() => {
                  if (window.confirm('Type LIVE in the next prompt to enable live trading.')) {
                    const conf = window.prompt('Type LIVE');
                    if (conf === 'LIVE') update('mode', 'live');
                  }
                }}
              />
              <strong>Live</strong>
              <p className="muted">Requires explicit confirmation.</p>
            </label>
          </div>
          <button
            className="btn"
            type="button"
            onClick={async () => {
              try {
                const { public_key } = await api.get<{ public_key: string }>('/push/vapid');
                const ok = await enableWebPush(public_key);
                alert(ok ? 'Web Push enabled' : 'Web Push not supported in this browser');
              } catch (e) {
                alert(`Push enable failed: ${e instanceof Error ? e.message : String(e)}`);
              }
            }}
          >
            Enable browser notifications
          </button>
          <label className="field">
            <span className="label">Paper bot interval</span>
            <select
              value={sub.data?.interval_seconds ?? 14400}
              disabled={updateInterval.isPending || current.mode !== 'paper'}
              onChange={(e) => updateInterval.mutate(Number(e.target.value))}
            >
              {paperIntervals.map((interval) => (
                <option key={interval.value} value={interval.value}>
                  {interval.label}
                </option>
              ))}
            </select>
          </label>
          <p className="muted">
            Next tick:{' '}
            {sub.data ? new Date(sub.data.next_tick_at).toLocaleTimeString() : 'loading…'}
          </p>
          <div className="card">
            <span className="label">Debug mode</span>
            <h3>Probe one tick now</h3>
            <p className="muted">
              Enqueues one immediate paper tick without changing your recurring interval.
            </p>
            <button
              className="btn"
              type="button"
              disabled={probeTick.isPending || current.mode !== 'paper'}
              onClick={() => probeTick.mutate()}
            >
              {probeTick.isPending ? 'Probing…' : 'Probe tick now'}
            </button>
          </div>
        </div>
      </section>

      <section className="panel panel-strong" style={{ marginTop: '1rem' }}>
        <div className="section-header">
          <div>
            <h2>Start paper trading</h2>
            <p className="muted">
              Saves this config in paper mode and schedules the first AI tick for the next cron run.
            </p>
          </div>
          <span className="badge badge-success">$10,000 paper equity</span>
        </div>
        <div className="row" style={{ justifyContent: 'flex-start' }}>
          <button
            className="btn btn-primary"
            type="button"
            disabled={startPaper.isPending}
            onClick={() => startPaper.mutate()}
          >
            {startPaper.isPending ? 'Starting…' : 'Start paper trading'}
          </button>
          <button
            className="btn"
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate(current)}
          >
            {save.isPending ? 'Saving…' : 'Save only'}
          </button>
        </div>
      </section>
      {save.error && <p style={{ color: 'crimson' }}>{(save.error as Error).message}</p>}
      {startPaper.error && (
        <p style={{ color: 'crimson' }}>{(startPaper.error as Error).message}</p>
      )}
      {updateInterval.error && (
        <p style={{ color: 'crimson' }}>{(updateInterval.error as Error).message}</p>
      )}
      {probeTick.error && <p style={{ color: 'crimson' }}>{(probeTick.error as Error).message}</p>}
      {probeTick.data && (
        <p className="badge badge-success" style={{ marginTop: '1rem' }}>
          Debug tick queued: {probeTick.data.tick_id.slice(0, 8)}…
        </p>
      )}
      {startPaper.data && (
        <p className="badge badge-success" style={{ marginTop: '1rem' }}>
          Paper trading scheduled. First tick:{' '}
          {new Date(startPaper.data.next_tick_at).toLocaleTimeString()}
        </p>
      )}
      {save.data?.notes && save.data.notes.length > 0 && (
        <ul style={{ color: '#a60' }}>
          {save.data.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      )}
      {startPaper.data?.notes && startPaper.data.notes.length > 0 && (
        <ul style={{ color: '#a60' }}>
          {startPaper.data.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      )}
    </main>
  );
};
